import { useMutation, useQueryClient } from "@tanstack/react-query";

import toast from "components/ui/toast/Toast";
import {
  SendLeadRequest,
  SendLeadRequestRecordKindEnum,
  SendOpportunityResponse,
} from "lib/generated/integration-api";
import { useIntegrationApi } from "lib/integrationApiContext";
import { refusedFieldsFrom } from "lib/integrations/crmSendErrors";
import { CrmPushRequest } from "lib/integrations/paragonCrm/types";

type SendParams = {
  guid: string;
  /** CRM field name -> value, already translated from the company's field mapping. */
  fields: Record<string, string | number>;
  /** The CRM object to create, from the mapping the form was built from. */
  entityName: string;
  /** CRM field name -> the record a lookup field points at. */
  lookups: CrmPushRequest["lookups"];
  /**
   * Which record is being pushed. This route serves leads and bids alike, and they may not go to
   * the same objects, so without it every push is checked against the lead's list.
   */
  recordKind: SendLeadRequestRecordKindEnum;
  /**
   * The chosen contact, translated through the company's contact mapping, for a CRM whose target
   * object holds no person of its own. Only a HubSpot lead needs one.
   */
  person?: CrmPushRequest;
};

/**
 * Pushes a lead or a bid to a CRM connected through Paragon, from the company's own field mapping.
 *
 * Separate from useSendOpportunity, which drives the providers we run OAuth for: its toast copy is
 * keyed by NativeProviderType, which has no entry for a CRM that only exists behind Paragon.
 */
/**
 * One record as the route takes it. Both maps travel as JSON strings keyed by the CRM's own field
 * names, and lookups stay separate because how a reference reaches a record differs per CRM:
 * Dynamics binds it to a path, Salesforce writes an id, HubSpot links it after the create.
 *
 * An empty lookups map is left off rather than sent as `{}`, which the route reads as none.
 */
function encodeRecord({
  logicalName,
  fields,
  lookups,
}: Pick<CrmPushRequest, "logicalName" | "fields" | "lookups">): {
  entityName: string;
  fields: string;
  lookups?: string;
} {
  return {
    entityName: logicalName,
    fields: JSON.stringify(fields),
    ...(Object.keys(lookups).length > 0 ? { lookups: JSON.stringify(lookups) } : {}),
  };
}

export function useSendParagonCrmRecord(
  guid: string,
  recordName: string,
  recordLabel: string,
  /** How the CRM is named in the toasts. */
  crmName: string,
) {
  const api = useIntegrationApi();
  const queryClient = useQueryClient();
  const shortName = recordName.slice(0, 30);
  const toastId = "paragonCrmRecord";

  return useMutation<SendOpportunityResponse, unknown, SendParams>(
    ({ guid: recordId, fields, entityName, lookups, recordKind, person }) => {
      const sendLeadRequest: SendLeadRequest = {
        guid: recordId,
        ...encodeRecord({ logicalName: entityName, fields, lookups }),
        recordKind,
      };

      if (person) {
        const encoded = encodeRecord(person);
        sendLeadRequest.personEntityName = encoded.entityName;
        sendLeadRequest.personFields = encoded.fields;
        sendLeadRequest.personLookups = encoded.lookups;
      }

      return api.sendLead({ sendLeadRequest });
    },
    {
      onMutate: () => {
        toast.loading(`Sending ${shortName}... to ${crmName}`, { id: toastId });
      },
      onSuccess: (data) => {
        toast.success(`${shortName}... sent to ${crmName}`, {
          action: data.externalUrl ? (
            <a href={data.externalUrl} target="_blank" rel="noreferrer">
              View in {crmName}
            </a>
          ) : undefined,
          duration: 5000,
        });
      },
      onError: async (error) => {
        // Which fields is left to the form, which is still open behind this and can name them under
        // the labels the user picked them by. A toast is gone before they can act on it.
        const refused = await refusedFieldsFrom(error);
        toast.error(
          refused.length > 0
            ? `${crmName} would not accept some of these fields`
            : `Something went wrong sending the ${recordLabel.toLowerCase()} to ${crmName}`,
        );
      },
      onSettled: () => {
        toast.dismiss(toastId);
        void queryClient.invalidateQueries({ queryKey: ["opportunityCrmDetails", guid] });
      },
    },
  );
}
